/**
 * `chats` — do'stlar va suhbatlar (SPEC §chats, faza 3).
 *
 * Ekran uch qatlamdan:
 *
 *   so'rovlar pilli      →  kelgan do'stlik so'rovlari (bo'lsa)
 *   onlayn qatori        →  gorizontal, holat nuqtasi bilan
 *   suhbatlar ro'yxati   →  oxirgi xabar + o'qilmagan hisoblagichi
 *
 * **Holat nuqtasi uch rangda** (SPEC): `--green` onlayn · `--danger`
 * efirda · `--gold` band. Oflayn odamga nuqta CHIZILMAYDI — kulrang
 * nuqta "onlayn emas" degan ma'noni bermaydi, u shunchaki shovqin.
 *
 * **ID bo'yicha do'st qo'shish bu yerda YO'Q.** Do'stlashish faqat
 * 1v1 da (`Call.tsx`, SPEC §call 201): begona ID'ni terib so'rov
 * yuborish tanishuv emas, u tasodifiy odamlarga spam yuborish yo'li
 * edi. Bo'sh holat matni ham shunga moslandi — u endi ID va'da
 * qilmaydi.
 */

import { useNavigate } from 'react-router-dom';

import { uz } from '../i18n/uz';
import { fmt } from '../lib/format';
import {
  useFriends,
  useOpenThread,
  useRespondToRequest,
  useThreads,
  type DmThreadRow,
  type FriendRow,
  type Presence,
} from '../lib/api/useSocial';
import { useRealtimeEvents } from '../lib/realtime/context';
import { Avatar, Toast } from '../ui';
import { useToast } from '../ui/useToast';
import './screens.css';

export function Chats() {
  const navigate = useNavigate();
  const toast = useToast();

  const friends = useFriends();
  const threads = useThreads();
  const respond = useRespondToRequest();
  const openThread = useOpenThread();

  // Yangi xabar yoki so'rov kelganda ro'yxat DARHOL yangilanadi —
  // aks holda odam ekranni qayta ochmaguncha ko'rmasdi.
  useRealtimeEvents((event) => {
    if (event.type === 'dm.message') void threads.refetch();
    if (event.type === 'friend.request') void friends.refetch();
  });

  const requests = friends.data?.incoming_requests ?? [];
  const online = (friends.data?.friends ?? []).filter((f) => f.presence !== 'offline');
  const items = threads.data?.items ?? [];

  async function openWith(userId: number): Promise<void> {
    try {
      const page = await openThread.mutateAsync(userId);
      const thread = page.items.find((t) => t.user_id === userId);
      if (thread) navigate(`/dm/${thread.thread_id}`);
    } catch {
      toast.show(uz.chats.failed);
    }
  }

  return (
    <div className="screen">
      {toast.message && <Toast message={toast.message} />}

      {/* Dockli ekran — o'z orqaga tugmasi YO'Q (SPEC §336). */}
      <div className="screen-bar">
        <h1 className="screen-bar__title">{uz.chats.title}</h1>
        {requests.length > 0 && (
          <div className="chats__pill tnum">{uz.chats.requests(fmt(requests.length))}</div>
        )}
      </div>

      {requests.length > 0 && (
        <div className="chats__requests">
          {requests.map((row) => (
            <RequestRow
              key={row.friendship_id}
              row={row}
              onAccept={() => respond.mutate({ id: row.friendship_id, action: 'accept' })}
              onReject={() => respond.mutate({ id: row.friendship_id, action: 'reject' })}
            />
          ))}
        </div>
      )}

      {online.length > 0 && (
        <>
          <div className="label" style={{ marginTop: 22 }}>
            {uz.chats.online}
          </div>
          <div className="chats__online">
            {online.map((friend) => (
              <button
                key={friend.user_id}
                type="button"
                className="chats__online-item"
                onClick={() => void openWith(friend.user_id)}
              >
                <div className="chats__avatar-wrap">
                  <Avatar
                    letter={friend.first_name.slice(0, 1)}
                    paletteIdx={friend.palette_idx}
                    size={54}
                  />
                  <PresenceDot presence={friend.presence} />
                </div>
                <span className="chats__online-name">{friend.first_name}</span>
              </button>
            ))}
          </div>
        </>
      )}

      <div className="label" style={{ marginTop: 22 }}>
        {uz.chats.conversations}
      </div>

      {threads.isLoading ? (
        <div className="empty">{uz.common.loading}</div>
      ) : items.length === 0 ? (
        // Bo'sh holat matnsiz qolmaydi (qoida 19). Matn ayni paytda
        // KEYINGI QADAMNI aytadi — "hech narsa yo'q" o'zi yordam bermaydi.
        <div className="empty">
          {(friends.data?.friends ?? []).length === 0 ? uz.chats.empty : uz.chats.emptyThreads}
        </div>
      ) : (
        <div className="chats__list">
          {items.map((thread) => (
            <ThreadRow
              key={thread.thread_id}
              thread={thread}
              onOpen={() => navigate(`/dm/${thread.thread_id}`)}
            />
          ))}
        </div>
      )}

      <div className="screen-tail" />
    </div>
  );
}

/* ── Holat nuqtasi ── */

function PresenceDot({ presence }: { presence: Presence }) {
  // Oflayn — nuqta YO'Q. Kulrang nuqta ma'no bermaydi, u shovqin.
  if (presence === 'offline') return null;

  const label =
    presence === 'live'
      ? uz.chats.presenceLive
      : presence === 'busy'
        ? uz.chats.presenceBusy
        : uz.chats.presenceOnline;

  return <span className={`presence presence--${presence}`} title={label} aria-label={label} />;
}

/* ── Kelgan so'rov ── */

function RequestRow({
  row,
  onAccept,
  onReject,
}: {
  row: FriendRow;
  onAccept: () => void;
  onReject: () => void;
}) {
  return (
    <div className="request-row">
      <Avatar letter={row.first_name.slice(0, 1)} paletteIdx={row.palette_idx} size={40} />
      <span className="request-row__name">{row.first_name}</span>
      <button type="button" className="request-row__reject" onClick={onReject}>
        {uz.chats.reject}
      </button>
      <button type="button" className="request-row__accept" onClick={onAccept}>
        {uz.chats.accept}
      </button>
    </div>
  );
}

/* ── Suhbat qatori ── */

function ThreadRow({ thread, onOpen }: { thread: DmThreadRow; onOpen: () => void }) {
  return (
    <button
      type="button"
      // O'qilmagan qatorning foni yoritiladi (SPEC §chats).
      className={thread.unread > 0 ? 'thread-row thread-row--unread' : 'thread-row'}
      onClick={onOpen}
    >
      <div className="chats__avatar-wrap">
        <Avatar letter={thread.first_name.slice(0, 1)} paletteIdx={thread.palette_idx} size={46} />
        <PresenceDot presence={thread.presence} />
      </div>

      <div className="thread-row__body">
        <div className="thread-row__name">{thread.first_name}</div>
        <div className="thread-row__last">{thread.last_message ?? uz.dm.empty}</div>
      </div>

      {thread.unread > 0 && <div className="thread-row__badge tnum">{fmt(thread.unread)}</div>}
    </button>
  );
}
